import jwt from 'jsonwebtoken';
import { ActionError } from '@/backend/errors/ActionError';
import { FriendlyError } from '@/backend/errors/FriendlyError';

/**
 * Everything the escape hatch knows about the request that broke the
 * bootstrap. `status`/`code` are best-effort — a thrown TypeError has
 * neither, a failed API request usually has both.
 */
export interface LoadFailure {
  message: string;
  /** HTTP status of the failed request, when known. */
  status?: number;
  /** FriendlyError canonical code, e.g. 'user-not-provisioned'. */
  code?: string;
}

export interface SwitchableCompany {
  companyId: string;
  companyName: string;
  roleLabel?: string;
}

/**
 * The slice of the UserContext singleton the escape hatch reads. All fields
 * are optional because any of the bootstrap requests may have failed before
 * populating them; `companyRoles` is `unknown` because User.set defaults it
 * to `{}` (not an array) until /api/user succeeds.
 */
export interface EscapeHatchUserContext {
  id?: string;
  companyId?: string;
  email?: string;
  company?: { id?: string; name?: string };
  companyRoles?: unknown;
  permissions?: { roleName?: string };
}

export type LoadFailureView =
  | { kind: 'generic' }
  | { kind: 'no-companies' }
  | {
      kind: 'lost-company-access';
      companyName?: string;
      companies: SwitchableCompany[];
    }
  | {
      kind: 'insufficient-permissions';
      companyName?: string;
      roleLabel?: string;
      /** True when the failed request was rejected as 401/403, so the
       * "insufficient permissions" wording is actually supported by evidence;
       * false for other failures (5xx, network), which get softer copy. */
      permissionDenied: boolean;
      otherCompanies: SwitchableCompany[];
    };

export const USER_NOT_PROVISIONED_CODE = 'user-not-provisioned';

/**
 * Decides which escape-hatch view to render from whatever state the bootstrap
 * reached before failing. The order matters: an unprovisioned login and an
 * empty companies list both mean "no companies", losing the current company
 * beats permission wording, and anything we can't classify falls back to the
 * generic screen.
 */
export function resolveLoadFailureView(
  userContext: EscapeHatchUserContext | undefined,
  failure: LoadFailure,
): LoadFailureView {
  if (failure.code === USER_NOT_PROVISIONED_CODE) {
    return { kind: 'no-companies' };
  }

  if (!userContext?.id) {
    return { kind: 'generic' };
  }

  const directRoles = normalizeCompanyRoles(userContext.companyRoles);
  if (directRoles.length === 0) {
    return { kind: 'no-companies' };
  }

  const currentRole = directRoles.find(
    (role) => role.companyId === userContext.companyId,
  );

  if (!currentRole) {
    return {
      kind: 'lost-company-access',
      companyName: userContext.company?.name,
      companies: directRoles,
    };
  }

  return {
    kind: 'insufficient-permissions',
    companyName: userContext.company?.name ?? currentRole.companyName,
    roleLabel:
      currentRole.roleLabel ??
      prettifyRoleName(userContext.permissions?.roleName),
    permissionDenied: failure.status === 401 || failure.status === 403,
    otherCompanies: directRoles.filter(
      (role) => role.companyId !== userContext.companyId,
    ),
  };
}

/**
 * Extracts the switchable companies from the raw companyRoles payload.
 * Filters to direct roles only: for @aiinsurance.io users the payload
 * contains every company in the system, which is not a useful switch list.
 */
export function normalizeCompanyRoles(value: unknown): SwitchableCompany[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const companies: SwitchableCompany[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const companyId =
      'companyId' in entry && typeof entry.companyId === 'string'
        ? entry.companyId
        : undefined;
    const companyName =
      'companyName' in entry && typeof entry.companyName === 'string'
        ? entry.companyName
        : undefined;
    const isDirectRole = 'isDirectRole' in entry && entry.isDirectRole === true;
    if (!companyId || !companyName || !isDirectRole) {
      continue;
    }

    const roleDisplayName =
      'roleDisplayName' in entry && typeof entry.roleDisplayName === 'string'
        ? entry.roleDisplayName
        : undefined;
    const roleName =
      'roleName' in entry && typeof entry.roleName === 'string'
        ? entry.roleName
        : undefined;

    companies.push({
      companyId,
      companyName,
      roleLabel: roleDisplayName ?? prettifyRoleName(roleName),
    });
  }
  return companies;
}

/**
 * Fallback when the friendly label (roles.display_name) isn't available:
 * turns a raw role enum like CLAIMS_MANAGER into "Claims Manager".
 */
export function prettifyRoleName(roleName?: string): string | undefined {
  if (!roleName) {
    return undefined;
  }
  return roleName
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Normalizes whatever the bootstrap threw into a LoadFailure. Request.ts
 * throws ActionError (with the HTTP status) or FriendlyError (with the
 * backend's canonical code); anything else only yields a message.
 */
export function loadFailureFromError(error: unknown): LoadFailure {
  const message = error instanceof Error ? error.message : String(error);
  const status =
    error instanceof ActionError ? error.httpStatusCode : undefined;
  const code =
    error instanceof FriendlyError ? error.canonicalErrorMessage : undefined;
  return { message, status, code };
}

/**
 * The user's email for "ask your admin to invite <email>" copy — from the
 * loaded user when /api/user succeeded, otherwise best-effort from the Auth0
 * token's claims.
 */
export function getSignedInEmail(
  userContext?: { email?: string },
  auth?: { getToken: () => unknown },
): string | undefined {
  if (userContext?.email) {
    return userContext.email;
  }
  try {
    const token = auth?.getToken();
    if (typeof token !== 'string' || !token) {
      return undefined;
    }
    const decoded = jwt.decode(token);
    if (!decoded || typeof decoded === 'string') {
      return undefined;
    }
    if (typeof decoded.email === 'string') {
      return decoded.email;
    }
    const namespacedEmail = Object.entries(decoded).find(
      ([key, value]) =>
        key.toLowerCase().endsWith('email') && typeof value === 'string',
    );
    return typeof namespacedEmail?.[1] === 'string'
      ? namespacedEmail[1]
      : undefined;
  } catch {
    return undefined;
  }
}
